import type { JSONContent } from '@tiptap/core';

/**
 * Convert markdown-style text to TipTap JSON format
 * Preserves headings, lists, bold, italic, code blocks, and paragraphs
 */
export function markdownToTipTap(text: string): JSONContent[] {
  const lines = text.split('\n');
  const nodes: JSONContent[] = [];
  let currentList: JSONContent | null = null;
  let currentListType: 'bulletList' | 'orderedList' | null = null;
  let inCodeBlock = false;
  let codeBlockContent: string[] = [];
  let codeBlockLanguage = '';
  let inTable = false;
  let tableRows: JSONContent[] = [];
  let isTableHeader = true;

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    const trimmed = line.trim();

    // Handle code blocks
    if (trimmed.startsWith('```')) {
      if (!inCodeBlock) {
        // Start code block
        inCodeBlock = true;
        codeBlockLanguage = trimmed.slice(3).trim();
        codeBlockContent = [];
      } else {
        // End code block
        inCodeBlock = false;
        nodes.push({
          type: 'codeBlock',
          attrs: codeBlockLanguage ? { language: codeBlockLanguage } : {},
          content: [{ type: 'text', text: codeBlockContent.join('\n') }],
        });
        codeBlockContent = [];
        codeBlockLanguage = '';
      }
      continue;
    }

    if (inCodeBlock) {
      codeBlockContent.push(line);
      continue;
    }

    // Close any open list if we hit a non-list line
    const isListItem = /^(\d+\.|-|\*|\+)\s/.test(trimmed);
    if (!isListItem && currentList) {
      nodes.push(currentList);
      currentList = null;
      currentListType = null;
    }

    // Handle headings
    if (trimmed.startsWith('####')) {
      nodes.push({
        type: 'heading',
        attrs: { level: 4 },
        content: parseInlineFormatting(trimmed.replace(/^####\s*/, '')),
      });
      continue;
    } else if (trimmed.startsWith('###')) {
      nodes.push({
        type: 'heading',
        attrs: { level: 3 },
        content: parseInlineFormatting(trimmed.replace(/^###\s*/, '')),
      });
      continue;
    } else if (trimmed.startsWith('##')) {
      nodes.push({
        type: 'heading',
        attrs: { level: 2 },
        content: parseInlineFormatting(trimmed.replace(/^##\s*/, '')),
      });
      continue;
    } else if (trimmed.startsWith('#')) {
      nodes.push({
        type: 'heading',
        attrs: { level: 1 },
        content: parseInlineFormatting(trimmed.replace(/^#\s*/, '')),
      });
      continue;
    }

    // Handle ordered lists
    const orderedMatch = trimmed.match(/^(\d+)\.\s+(.+)/);
    if (orderedMatch) {
      const itemContent = orderedMatch[2];
      
      if (currentListType !== 'orderedList') {
        // Close previous list if different type
        if (currentList) nodes.push(currentList);
        
        currentList = {
          type: 'orderedList',
          content: [],
        };
        currentListType = 'orderedList';
      }

      currentList.content!.push({
        type: 'listItem',
        content: [
          {
            type: 'paragraph',
            content: parseInlineFormatting(itemContent),
          },
        ],
      });
      continue;
    }

    // Handle unordered lists
    const unorderedMatch = trimmed.match(/^[-*+]\s+(.+)/);
    if (unorderedMatch) {
      const itemContent = unorderedMatch[1];
      
      if (currentListType !== 'bulletList') {
        // Close previous list if different type
        if (currentList) nodes.push(currentList);
        
        currentList = {
          type: 'bulletList',
          content: [],
        };
        currentListType = 'bulletList';
      }

      currentList.content!.push({
        type: 'listItem',
        content: [
          {
            type: 'paragraph',
            content: parseInlineFormatting(itemContent),
          },
        ],
      });
      continue;
    }

    // Handle markdown tables
    if (trimmed.startsWith('|') && trimmed.endsWith('|')) {
      // Check if this is a separator line
      if (trimmed.match(/^\|[\s\-:|]+\|$/)) {
        isTableHeader = false; // Next rows will be body rows
        continue;
      }
      
      if (!inTable) {
        inTable = true;
        isTableHeader = true;
        tableRows = [];
      }
      
      // Parse table cells
      const cells = trimmed.slice(1, -1).split('|').map(c => c.trim());
      const cellType = isTableHeader ? 'tableHeader' : 'tableCell';
      
      const rowContent: JSONContent[] = cells.map(cellText => ({
        type: cellType,
        content: [
          {
            type: 'paragraph',
            content: parseInlineFormatting(cellText),
          },
        ],
      }));
      
      tableRows.push({
        type: 'tableRow',
        content: rowContent,
      });
      
      continue;
    } else if (inTable) {
      // End of table - add it to nodes
      if (tableRows.length > 0) {
        nodes.push({
          type: 'table',
          content: tableRows,
        });
        tableRows = [];
      }
      inTable = false;
      isTableHeader = true;
    }

    // Handle horizontal rules
    if (trimmed === '---' || trimmed === '***' || trimmed === '___') {
      nodes.push({ type: 'horizontalRule' });
      continue;
    }

    // Handle empty lines
    if (trimmed === '') {
      // Only add empty paragraph if not consecutive
      if (nodes.length === 0 || nodes[nodes.length - 1].type !== 'paragraph' || nodes[nodes.length - 1].content?.length) {
        nodes.push({
          type: 'paragraph',
          content: [],
        });
      }
      continue;
    }

    // Handle regular paragraphs
    nodes.push({
      type: 'paragraph',
      content: parseInlineFormatting(line),
    });
  }

  // Close any remaining list
  if (currentList) {
    nodes.push(currentList);
  }
  
  // Close any remaining table
  if (inTable && tableRows.length > 0) {
    nodes.push({
      type: 'table',
      content: tableRows,
    });
  }

  // Close any unclosed code block
  if (inCodeBlock && codeBlockContent.length > 0) {
    nodes.push({
      type: 'codeBlock',
      attrs: codeBlockLanguage ? { language: codeBlockLanguage } : {},
      content: [{ type: 'text', text: codeBlockContent.join('\n') }],
    });
  }

  return nodes.length > 0 ? nodes : [{ type: 'paragraph', content: [] }];
}

/**
 * Parse inline formatting: bold, italic, code, underline
 */
function parseInlineFormatting(text: string): JSONContent[] {
  if (!text) return [];

  const content: JSONContent[] = [];
  let currentIndex = 0;

  // Regex to match **bold**, *italic*, `code`, ~underline~, and [Source: ...]
  const inlineRegex = /(\*\*(.+?)\*\*|\*(.+?)\*|`(.+?)`|~(.+?)~|\[Source:\s*(.+?)\])/g;
  let match;

  while ((match = inlineRegex.exec(text)) !== null) {
    // Add text before the match
    if (match.index > currentIndex) {
      const plainText = text.substring(currentIndex, match.index);
      content.push({ type: 'text', text: plainText });
    }

    if (match[2]) {
      // Bold text (**text**)
      content.push({
        type: 'text',
        text: match[2],
        marks: [{ type: 'bold' }],
      });
    } else if (match[3]) {
      // Italic text (*text*)
      content.push({
        type: 'text',
        text: match[3],
        marks: [{ type: 'italic' }],
      });
    } else if (match[4]) {
      // Code text (`code`)
      content.push({
        type: 'text',
        text: match[4],
        marks: [{ type: 'code' }],
      });
    } else if (match[5]) {
      // Underline text (~text~)
      content.push({
        type: 'text',
        text: match[5],
        marks: [{ type: 'highlight' }],
      });
    } else if (match[6]) {
      // Citation [Source: ...]
      content.push({
        type: 'text',
        text: ` [Source: ${match[6]}]`,
        marks: [{ type: 'italic' }],
      });
    }

    currentIndex = match.index + match[0].length;
  }

  // Add remaining text
  if (currentIndex < text.length) {
    const remainingText = text.substring(currentIndex);
    content.push({ type: 'text', text: remainingText });
  }

  return content.length > 0 ? content : [{ type: 'text', text }];
}

/**
 * Convert TipTap JSON content to plain markdown text
 */
export function tipTapToMarkdown(content: JSONContent): string {
  if (!content.content || content.content.length === 0) return '';

  return content.content.map((node) => nodeToMarkdown(node)).join('\n');
}

function nodeToMarkdown(node: JSONContent): string {
  switch (node.type) {
    case 'heading': {
      const level = node.attrs?.level || 1;
      const headingMarks = '#'.repeat(level);
      return `${headingMarks} ${contentToText(node.content || [])}`;
    }

    case 'paragraph':
      return contentToText(node.content || []);

    case 'bulletList':
      return (node.content || [])
        .map((item) => {
          const text = nodeToMarkdown(item);
          return `- ${text}`;
        })
        .join('\n');

    case 'orderedList':
      return (node.content || [])
        .map((item, index) => {
          const text = nodeToMarkdown(item);
          return `${index + 1}. ${text}`;
        })
        .join('\n');

    case 'listItem':
      return (node.content || []).map((n) => nodeToMarkdown(n)).join('');

    case 'codeBlock': {
      const language = node.attrs?.language || '';
      const code = contentToText(node.content || []);
      return `\`\`\`${language}\n${code}\n\`\`\``;
    }

    case 'horizontalRule':
      return '---';

    case 'hardBreak':
      return '\n';

    default:
      return contentToText(node.content || []);
  }
}

function contentToText(content: JSONContent[]): string {
  return content
    .map((node) => {
      if (node.type === 'text') {
        let text = node.text || '';
        
        // Apply marks
        if (node.marks) {
          node.marks.forEach((mark) => {
            switch (mark.type) {
              case 'bold':
                text = `**${text}**`;
                break;
              case 'italic':
                text = `*${text}*`;
                break;
              case 'code':
                text = `\`${text}\``;
                break;
              case 'highlight':
                text = `~${text}~`;
                break;
            }
          });
        }
        
        return text;
      }
      return nodeToMarkdown(node);
    })
    .join('');
}
